// packages/ui/src/hooks/useVendorStaffRole.ts
//
// vg-binding: P18.D-01
// vg-binding: P18.D-21
//
// Phase 18 Task 09 — the vendor portal's missing permission axis.
//
// Why this exists: the vendor portal ships zero `usePermission()` call sites,
// and the vendor-side permission catalog (STAFF_PERMISSIONS) is not enforced
// anywhere. P18.D-01 settles the question — the axis that actually decides who
// may mutate integration settings is `VendorMembership.role`: `owner` manages
// API keys + webhook URL, the other 5 roles (operator/qc/packaging/finance/
// viewer) are read-only. This hook is the single place that resolves it.
//
// Deliberately NOT a binary `isStaff` boolean: `role` is surfaced verbatim so a
// screen can show the real role, and so widening a grant later ("finance may
// see the delivery log") is a change here, not a re-derivation everywhere.
//
// Admin is out of scope by design — the admin portal reuses the already-seeded
// `vendors:view` / `vendors:manage` permissions through `usePermission()`, so
// P18.D-21's admin tab needs no new seed work.

import { useMemo } from 'react';

import {
  useAuth,
  type VendorMembershipRole,
  type VendorMembershipSummary,
} from './useAuth.js';

/** Stable empty tuple — a fresh `[]` per render would defeat the useMemo dep. */
const NO_MEMBERSHIPS: readonly VendorMembershipSummary[] = Object.freeze([]);

export interface VendorStaffRoleResult {
  /** Role on the resolved vendor, or null when the user is not a member. */
  role: VendorMembershipRole | null;
  /** Vendor the role was resolved against (portal scope, arg, or sole membership). */
  vendorId: string | null;
  /** True when an active membership exists for the resolved vendor. */
  isMember: boolean;
  /** P18.D-01 — only the owner may mutate keys / webhook config. */
  isOwner: boolean;
  /**
   * Gate for mutate controls (create key, revoke, verify+save webhook, rotate
   * secret). Equals `isOwner` today; kept as its own field so call sites read
   * as intent ("may this user manage?") instead of restating the rule.
   */
  canManage: boolean;
  /** Member, but not the owner → render controls disabled, not hidden. */
  isReadOnly: boolean;
  /** All active vendor memberships — feeds the multi-vendor picker (P18.D-02). */
  memberships: readonly VendorMembershipSummary[];
}

/**
 * Resolve the caller's role on a vendor.
 *
 * Vendor resolution order:
 *   1. explicit `vendorId` argument (multi-vendor owner picking a vendor),
 *   2. the portal's active scope (`AuthUser.vendorId`),
 *   3. the sole membership when the user belongs to exactly one vendor.
 *
 * A multi-vendor user with no explicit choice and no portal scope resolves to
 * `null` — which lands on read-only, the safe default. P18.D-02 requires an
 * explicit vendor choice before minting a key, so refusing to guess is the
 * point, not a gap.
 */
export function useVendorStaffRole(
  vendorId?: string | null,
): VendorStaffRoleResult {
  // Two narrow selectors: each returns a stable reference (the array identity
  // from the store) or a primitive. Selecting a freshly-built object here would
  // break useSyncExternalStore's snapshot caching in zustand v5.
  const memberships = useAuth((state) => state.user?.vendorMemberships);
  const portalVendorId = useAuth((state) => state.user?.vendorId ?? null);

  return useMemo<VendorStaffRoleResult>(() => {
    const list: readonly VendorMembershipSummary[] =
      memberships ?? NO_MEMBERSHIPS;

    const soleVendorId = list.length === 1 ? (list[0]?.vendorId ?? null) : null;
    const resolvedVendorId = vendorId ?? portalVendorId ?? soleVendorId;

    const membership =
      resolvedVendorId === null
        ? undefined
        : list.find((entry) => entry.vendorId === resolvedVendorId);

    const role = membership?.role ?? null;
    const isMember = membership !== undefined;
    const isOwner = role === 'owner';

    return {
      role,
      vendorId: resolvedVendorId,
      isMember,
      isOwner,
      canManage: isOwner,
      isReadOnly: isMember && !isOwner,
      memberships: list,
    };
  }, [memberships, portalVendorId, vendorId]);
}
